import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatCurrency, StatusBadge } from '../utils/helpers';
import { AlertOctagon, CheckCircle, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AppModal } from '../components/common/AppModal';

export default function Disputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [resolveModal, setResolveModal] = useState(null);
  const [resolution, setResolution] = useState('');
  const { isAdmin, isFinance } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    api.get('/disputes', { params: { status: statusFilter || undefined } })
      .then(r => setDisputes(r.data.disputes || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleResolve = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/disputes/${resolveModal.dispute_id}/resolve`, { resolution });
      toast.success('Dispute resolved');
      setResolveModal(null);
      setResolution('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resolve');
    }
  };

  const statuses = ['', 'OPEN', 'UNDER_REVIEW', 'RESOLVED', 'ESCALATED'];

  const statusColors = {
    OPEN: 'bg-red-100 text-red-700',
    UNDER_REVIEW: 'bg-yellow-100 text-yellow-700',
    RESOLVED: 'bg-green-100 text-green-700',
    ESCALATED: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <AlertOctagon size={22} className="text-red-500" />
          <h2 className="text-xl font-semibold">Disputes</h2>
        </div>
        <select className="select max-w-xs text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {statuses.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['OPEN', 'UNDER_REVIEW', 'RESOLVED', 'ESCALATED'].map(s => {
          const count = disputes.filter(d => d.status === s).length;
          return (
            <div key={s} className="card p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter(s)}>
              <div className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${statusColors[s]}`}>{s.replace(/_/g,' ')}</div>
              <div className="text-2xl font-bold">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>{['Invoice','Reason','Raised By','Status','Date','Actions'].map(h => <th key={h} className="table-th">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="table-td text-center text-gray-400">Loading…</td></tr>
            ) : disputes.length === 0 ? (
              <tr><td colSpan={6} className="table-td text-center py-10">
                <AlertOctagon size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-400">No disputes found</p>
              </td></tr>
            ) : disputes.map(d => (
              <tr key={d.dispute_id} className="hover:bg-gray-50">
                <td className="table-td">
                  <button onClick={() => navigate(`/invoices/${d.invoice_id}`)} className="text-blue-600 hover:underline font-mono text-sm">
                    {d.invoice_number || d.invoice_id?.slice(0,8)}
                  </button>
                </td>
                <td className="table-td max-w-xs">
                  <p className="truncate text-sm" title={d.reason}>{d.reason}</p>
                </td>
                <td className="table-td text-gray-500">{d.raised_by_name}</td>
                <td className="table-td">
                  <span className={`badge ${statusColors[d.status]}`}>{d.status.replace(/_/g,' ')}</span>
                </td>
                <td className="table-td text-gray-500">{formatDate(d.created_at)}</td>
                <td className="table-td">
                  {(isAdmin || isFinance) && d.status !== 'RESOLVED' && (
                    <button onClick={() => setResolveModal(d)} className="btn-secondary text-xs py-1 px-2">
                      <CheckCircle size={12} /> Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resolve Modal */}
      {resolveModal && (
        <AppModal
          title="Resolve Dispute"
          subtitle={`Invoice ${resolveModal.invoice_number} — ${resolveModal.reason}`}
          onClose={() => { setResolveModal(null); setResolution(''); }}
          maxWidth={448}
          footer={(
            <>
              <button type="button" onClick={() => { setResolveModal(null); setResolution(''); }} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" form="resolve-dispute-form" className="btn-primary flex-1 justify-center"><CheckCircle size={14} /> Resolve</button>
            </>
          )}
        >
            <form id="resolve-dispute-form" onSubmit={handleResolve} className="space-y-4">
              <div>
                <label className="label">Resolution *</label>
                <textarea className="input" rows={3} value={resolution} onChange={e => setResolution(e.target.value)} required
                  placeholder="Describe how the dispute was resolved..." />
              </div>
            </form>
        </AppModal>
      )}
    </div>
  );
}
